
import { useMemo } from 'react';
import { DebtEvent, DebtRecord, Property, PropertyDebtLink, YearData } from '../types';
import { buildModel, calculateIRR, calculateTotalInitialInvestment } from '../services/financials';
import { formatCurrency0, formatCurrency2, formatPercent2, formatNumber2 } from '../utils/format';
import { Info } from 'lucide-react';
import { buildDebtSnapshotForProperty } from '../services/debt';

interface PortfolioSpreadsheetViewProps {
  properties: Property[];
  allProperties: Record<string, Property>;
  debts: Record<string, DebtRecord>;
  debtLinks: PropertyDebtLink[];
  debtEvents: DebtEvent[];
}

const PortfolioSpreadsheetView = (props: PortfolioSpreadsheetViewProps) => {
  const { properties, allProperties, debts, debtLinks, debtEvents } = props;
  const aggregatedProForma = useMemo(() => {
    if (properties.length === 0) return [];

    // Calculate models for all properties
    const models = properties.map((p: any) => buildModel(p.currentYearInputs || p.inputs, p.riskProfile, buildDebtSnapshotForProperty(p.id, allProperties, debts, debtLinks, debtEvents), { baseYear: p.baseYear, historicalActuals: p.historicalActuals }, { modelEvents: p.modelEvents || [] }));
    const totalEquity = models.reduce((sum: any, model: any) => sum + model.metrics.totalInitialInvestment, 0);
    const maxHoldPeriod = Math.max(...models.map((model: any) => model.proForma.length));

    const result: YearData[] = [];
    const annualNetCfHistory: number[] = [];

    for (let yearIdx = 0; yearIdx < maxHoldPeriod; yearIdx++) {
      const year = yearIdx + 1;
      
      let yearEgi = 0;
      let yearExpenses = 0;
      let yearNoi = 0;
      let yearInterestPaid = 0;
      let yearPrincipalPaid = 0;
      let yearDebtService = 0;
      let yearAppreciation = 0;
      let yearOperatingCashFlowBeforeReserves = 0;
      let yearCapexReserve = 0;
      let yearOperatingCf = 0;
      let yearTrueReturn = 0;
      let yearLoanBalance = 0;
      let yearSalePrice = 0;
      let yearSaleProceeds = 0;
      let yearReserveRelease = 0;
      let yearCumulativePrincipalPaid = 0;

      models.forEach((model: any) => {
        // If property hold period is shorter than current year, it's considered "sold" or inactive
        const yearData = model.proForma[yearIdx];
        if (yearData) {
          yearEgi += yearData.egi;
          yearExpenses += yearData.expenses;
          yearNoi += yearData.noi;
          yearInterestPaid += yearData.interestPaid;
          yearPrincipalPaid += yearData.principalPaid;
          yearDebtService += yearData.debtService;
          yearAppreciation += yearData.appreciation;
          yearOperatingCashFlowBeforeReserves += yearData.operatingCashFlowBeforeReserves;
          yearCapexReserve += yearData.capexReserve;
          yearOperatingCf += yearData.operatingCf;
          yearTrueReturn += yearData.trueReturn;
          yearLoanBalance += yearData.loanBalance;
          yearSalePrice += yearData.assumedSalePrice;
          yearSaleProceeds += yearData.netSaleProceeds;
          yearReserveRelease += yearData.reserveRelease;
          yearCumulativePrincipalPaid += yearData.cumulativePrincipalPaid;
        }
      });

      annualNetCfHistory.push(yearOperatingCf);
      
      const cumulativeNetCf = annualNetCfHistory.reduce((sum, cf) => sum + cf, 0);
      const coc = (yearOperatingCf / Math.max(1, totalEquity)) * 100;
      const moic = (cumulativeNetCf + yearSaleProceeds) / Math.max(1, totalEquity);
      
      // Calculate IRR if sold at the end of this year
      const irrCashFlows = [-totalEquity, ...annualNetCfHistory.slice(0, -1), yearOperatingCf + yearSaleProceeds];
      const irrRate = calculateIRR(irrCashFlows);
      const irr = irrRate === null ? null : irrRate * 100;

      result.push({
        year,
        months: Array.from({ length: 12 }, (_, monthOffset) => (yearIdx * 12) + monthOffset + 1),
        grossRent: 0,
        egi: yearEgi,
        expenses: yearExpenses,
        noi: yearNoi,
        interestPaid: yearInterestPaid,
        principalPaid: yearPrincipalPaid,
        debtService: yearDebtService,
        appreciation: yearAppreciation,
        operatingCashFlowBeforeReserves: yearOperatingCashFlowBeforeReserves,
        capexReserve: yearCapexReserve,
        operatingCf: yearOperatingCf,
        trueReturn: yearTrueReturn,
        reserveRelease: yearReserveRelease,
        loanBalance: yearLoanBalance,
        cumulativePrincipalPaid: yearCumulativePrincipalPaid,
        assumedSalePrice: yearSalePrice,
        netSaleProceeds: yearSaleProceeds,
        coc,
        moic,
        irr,
        equityFromSale: yearSaleProceeds,
        totalCashFlow: yearOperatingCf + yearSaleProceeds + yearReserveRelease
      });
    }

    return result;
  }, [properties, allProperties, debts, debtLinks, debtEvents]);

  if (aggregatedProForma.length === 0) return null;

  return (
    <div className="rounded-3xl border border-slate-700/70 bg-slate-900 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[11px] min-w-[1200px]">
          <thead>
            <tr className="bg-slate-900 text-[#9ca3af] font-black uppercase text-[9px] tracking-[0.15em] border-b border-slate-700">
              <th className="px-6 py-5 sticky left-0 bg-slate-900 z-10">Year</th>
              <th className="px-6 py-5 text-right">EGI</th>
              <th className="px-6 py-5 text-right text-[#ef4444]">OpEx</th>
              <th className="px-6 py-5 text-right">NOI</th>
              <th className="px-6 py-5 text-right text-[#ef4444]">Debt Svc</th>
              <th className="px-6 py-5 text-right text-[#ef4444]">CapEx Res</th>
              <th className="px-6 py-5 text-right text-[#22c55e]">Net CF</th>
              <th className="px-6 py-5 text-right">Loan Bal</th>
              <th className="px-6 py-5 text-right group relative cursor-help">
                SALE PRICE
                <Info size={10} className="inline ml-1 opacity-40" />
                <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-3 bg-slate-900 text-white text-[8px] rounded-lg w-48 normal-case tracking-normal z-50 shadow-2xl">
                  Portfolio sum of individual assumed sale prices.
                </div>
              </th>
              <th className="px-6 py-5 text-right group relative cursor-help">
                SALE PROCEEDS
                <Info size={10} className="inline ml-1 opacity-40" />
                <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-3 bg-slate-900 text-white text-[8px] rounded-lg w-48 normal-case tracking-normal z-50 shadow-2xl">
                  Portfolio sum of net proceeds after selling costs and debt payoff.
                </div>
              </th>
              <th className="px-6 py-5 text-right">Reserve Release</th>
              <th className="px-6 py-5 text-right text-[#22c55e] bg-emerald-500/10">CoC %</th>
              <th className="px-6 py-5 text-right">MOIC</th>
              <th className="px-6 py-5 text-right">Exit IRR</th>
            </tr>
          </thead>
          <tbody>
            {aggregatedProForma.map((row: any) => (
              <tr key={row.year} className="hover:bg-slate-800/40 transition-colors border-b border-slate-800 group">
                <td className="px-6 py-4 font-black sticky left-0 bg-slate-900 group-hover:bg-slate-800/40 z-10 text-[#f9fafb]">{row.year}</td>
                <td className="px-6 py-4 font-mono text-right text-[#f9fafb]">{formatCurrency2(row.egi)}</td>
                <td className="px-6 py-4 font-mono text-right text-[#ef4444]">-{formatCurrency2(row.expenses)}</td>
                <td className="px-6 py-4 font-bold text-right text-[#22c55e]">{formatCurrency2(row.noi)}</td>
                <td className="px-6 py-4 font-mono text-right text-[#ef4444]">-{formatCurrency2(row.debtService)}</td>
                <td className="px-6 py-4 font-mono text-right text-[#ef4444]">-{formatCurrency2(row.capexReserve)}</td>
                <td className={`px-6 py-4 font-black text-right ${row.operatingCf >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>{formatCurrency2(row.operatingCf)}</td>
                <td className="px-6 py-4 font-mono text-right text-[#9ca3af]">{formatCurrency2(row.loanBalance)}</td>
                <td className="px-6 py-4 font-mono text-right text-[#f9fafb]">{formatCurrency0(row.assumedSalePrice)}</td>
                <td className="px-6 py-4 font-bold text-right text-[#f9fafb]">{formatCurrency0(row.netSaleProceeds)}</td>
                <td className="px-6 py-4 font-mono text-right text-[#9ca3af]">{formatCurrency0(row.reserveRelease)}</td>
                <td className="px-6 py-4 text-[#22c55e] font-black text-right bg-emerald-500/10">{formatPercent2(row.coc)}</td>
                <td className="px-6 py-4 text-[#f9fafb] font-bold text-right">{formatNumber2(row.moic)}x</td>
                <td className="px-6 py-4 text-[#f9fafb] font-black text-right">{formatPercent2(row.irr)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-950 text-[#f9fafb] font-black uppercase text-[10px] tracking-widest border-t border-slate-700">
              <td className="px-6 py-5">Totals</td>
              <td className="px-6 py-5 text-right font-mono text-[#9ca3af]">-</td>
              <td className="px-6 py-5 text-right font-mono text-[#9ca3af]">-</td>
              <td className="px-6 py-5 text-right text-[#22c55e]">{formatCurrency0(aggregatedProForma.reduce((a: any, b: any) => a + b.noi, 0))}</td>
              <td className="px-6 py-5 text-right text-[#ef4444]">-{formatCurrency0(aggregatedProForma.reduce((a: any, b: any) => a + b.debtService, 0))}</td>
              <td className="px-6 py-5 text-right text-[#ef4444]">-{formatCurrency0(aggregatedProForma.reduce((a: any, b: any) => a + b.capexReserve, 0))}</td>
              <td className={`px-6 py-5 text-right ${aggregatedProForma.reduce((a: any, b: any) => a + b.operatingCf, 0) >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>{formatCurrency0(aggregatedProForma.reduce((a: any, b: any) => a + b.operatingCf, 0))}</td>
              <td className="px-6 py-5 text-right font-mono text-[#9ca3af]">-</td>
              <td className="px-6 py-5 text-right whitespace-nowrap">FINAL: {formatCurrency0(aggregatedProForma[aggregatedProForma.length-1].assumedSalePrice)}</td>
              <td className="px-6 py-5 text-right whitespace-nowrap">FINAL: {formatCurrency0(aggregatedProForma[aggregatedProForma.length-1].netSaleProceeds)}</td>
              <td className="px-6 py-5 text-right whitespace-nowrap">FINAL: {formatCurrency0(aggregatedProForma[aggregatedProForma.length-1].reserveRelease)}</td>
              <td className="px-6 py-5 text-right text-[#22c55e]">AVG: {formatPercent2(aggregatedProForma.reduce((a: any, b: any) => a + b.coc, 0) / Math.max(1, aggregatedProForma.length))}</td>
              <td className="px-6 py-5 text-right text-[#f9fafb]">FINAL: {formatNumber2(aggregatedProForma[aggregatedProForma.length-1].moic)}x</td>
              <td className="px-6 py-5 text-right text-[#f9fafb]">FINAL: {formatPercent2(aggregatedProForma[aggregatedProForma.length-1].irr)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PortfolioSpreadsheetView;
